import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    ProgressBar,
    Badge,
    Button,
    Accordion,
    Form,
    Alert,
} from "react-bootstrap";

import "./bookDashboardDetails.css";
import { clearData } from "../../../utils/localStorage";
import {
    bookPurchasedDetailService,
    bookProgressService,
    bookReviewsService,
    bookBookmarkListService,
    bookNoteListService,
    createBookReviewService,
    deleteBookBookmarkService,
    deleteBookNoteService,
    updateBookReviewService,
    deleteBookReviewService,
} from "../../../modules/services";
import SideBar from "../../commanComponents/sidebar/sidebar";
import { sidebarNavItems, ROUTES } from "../../../constant";
import TopNavigation from "../../commanComponents/topNavigation/topNavigation";

const defaultReviewForm = {
    rating: 0,
    title: "",
    comment: "",
};

const statusCopy = {
    completed: "Completed",
    in_progress: "In Progress",
    not_started: "Not Started",
    locked: "Locked",
};

const getStatus = (progress, completedAt) => {
    if (completedAt || progress >= 100) return "completed";
    if (progress > 0) return "in_progress";
    return "not_started";
};

const formatDate = (value) => {
    if (!value) return "Not available";

    return new Date(value).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

const formatTime = (seconds) => {
    if (!seconds) return "0 min";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    return `${minutes} min`;
};

const formatLessonType = (type) => {
    if (!type) return "Lesson";

    return type
        .toString()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getAuthorName = (review) => {
    const firstName = review?.user?.first_name?.trim() || "";
    const lastName = review?.user?.last_name?.trim() || "";
    const fullName = [firstName, lastName].filter(Boolean).join(" ");

    return fullName || review?.user?.email || "Student";
};

const PurchasedBookDetails = () => {
    const navigate = useNavigate();
    const { slug } = useParams();
    const contentRef = useRef(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [bookData, setBookData] = useState(null);
    const [progressData, setProgressData] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [bookmarks, setBookmarks] = useState([]);
    const [notes, setNotes] = useState([]);
    const [activeTab, setActiveTab] = useState("chapters");
    const [pageError, setPageError] = useState("");
    const [reviewForm, setReviewForm] = useState(defaultReviewForm);
    const [reviewErrors, setReviewErrors] = useState({});
    const [reviewMessage, setReviewMessage] = useState({ type: "", text: "" });

    const syncReviewForm = useCallback((review) => {
        if (!review) {
            setReviewForm(defaultReviewForm);
            return;
        }

        setReviewForm({
            rating: Number(review.rating || 0),
            title: review.title || "",
            comment: review.comment || "",
        });
    }, []);

    const loadPageData = useCallback(async () => {
        setIsLoading(true);
        setPageError("");

        try {
            const detailResponse = await bookPurchasedDetailService(slug, {});

            if (detailResponse === "error") {
                clearData();
                navigate(ROUTES.LOGIN);
                return;
            }

            if (!(detailResponse.status_code === 200 || detailResponse.status_code === 201)) {
                throw new Error(detailResponse?.message?.[0] || "Unable to load this book.");
            }

            const detailData = detailResponse.data;
            setBookData(detailData);
            syncReviewForm(detailData.current_review);

            if (Array.isArray(detailData.reviews)) {
                setReviews(detailData.reviews);
            } else if (detailData?.book?.id) {
                const reviewsResponse = await bookReviewsService(detailData.book.id, { pagination: false });
                if (reviewsResponse !== "error" && (reviewsResponse.status_code === 200 || reviewsResponse.status_code === 201)) {
                    setReviews(Array.isArray(reviewsResponse.data) ? reviewsResponse.data : []);
                }
            }

            if (detailData?.book?.id) {
                const bookmarksResponse = await bookBookmarkListService(detailData.book.id, { pagination: false });
                if (bookmarksResponse === "error") {
                    clearData();
                    navigate(ROUTES.LOGIN);
                    return;
                }
                if (bookmarksResponse.status_code === 200 || bookmarksResponse.status_code === 201) {
                    setBookmarks(Array.isArray(bookmarksResponse.data) ? bookmarksResponse.data : []);
                } else {
                    setBookmarks([]);
                }
            }

            if (detailData?.book?.id) {
                const notesResponse = await bookNoteListService(detailData.book.id, { pagination: false });
                if (notesResponse === "error") {
                    clearData();
                    navigate(ROUTES.LOGIN);
                    return;
                }
                if (notesResponse.status_code === 200 || notesResponse.status_code === 201) {
                    setNotes(Array.isArray(notesResponse.data) ? notesResponse.data : []);
                } else {
                    setNotes([]);
                }
            }

            if (!Array.isArray(detailData.chapters) && detailData?.book?.id) {
                const progressResponse = await bookProgressService(detailData.book.id, {});
                if (progressResponse !== "error" && (progressResponse.status_code === 200 || progressResponse.status_code === 201)) {
                    setProgressData(progressResponse.data);
                }
            }
        } catch (error) {
            console.error("Error loading purchased book details:", error);
            setPageError(error?.message || "Unable to load this book.");
        } finally {
            setIsLoading(false);
        }
    }, [navigate, slug, syncReviewForm]);

    useEffect(() => {
        loadPageData();
    }, [loadPageData]);

    const book = bookData?.book;
    const currentReview = bookData?.current_review;
    const canReview = bookData?.can_review ?? !currentReview;
    const progress = Number(bookData?.overall_progress || progressData?.overall_progress || 0);
    const learningStatus = getStatus(progress, bookData?.completed_at);

    const bookmarkedLessonIds = useMemo(() => (
        new Set(
            (Array.isArray(bookmarks) ? bookmarks : [])
                .map((bookmark) => Number(bookmark.lesson_id || 0))
                .filter(Boolean)
        )
    ), [bookmarks]);

    const chapters = useMemo(() => {
        const source = Array.isArray(bookData?.chapters)
            ? bookData.chapters
            : Array.isArray(progressData?.chapters)
                ? progressData.chapters
                : [];

        const chapterRows = source
            .map((chapter, chapterIndex) => {
                const lessons = (Array.isArray(chapter.lessons) ? chapter.lessons : [])
                    .map((lesson, lessonIndex) => ({
                        id: lesson.id || `${chapter.id || chapterIndex}-${lessonIndex}`,
                        title: lesson.title || `Lesson ${lessonIndex + 1}`,
                        order: Number(lesson.order || lessonIndex + 1),
                        content_type: formatLessonType(lesson.content_type),
                        duration_minutes: Number(lesson.duration_minutes || 0),
                        status: lesson.status || "not_started",
                        access_count: Number(lesson.access_count || 0),
                        time_spent_seconds: Number(lesson.time_spent_seconds || 0),
                    }))
                    .sort((first, second) => first.order - second.order);

                const totalLessons = Number(chapter.total_lessons || lessons.length || chapter.lesson_count || 0);
                const completedLessons = Number(
                    chapter.completed_lessons ||
                    lessons.filter((lesson) => lesson.status === "completed").length ||
                    0
                );
                const chapterProgress = Number(
                    chapter.progress_percentage ||
                    (totalLessons ? Math.round((completedLessons / totalLessons) * 100) : 0)
                );

                return {
                    id: chapter.id || `chapter-${chapterIndex}`,
                    title: chapter.title || chapter.chapter_title || `Chapter ${chapterIndex + 1}`,
                    order: Number(chapter.order || chapterIndex + 1),
                    description: chapter.description || "",
                    total_lessons: totalLessons,
                    completed_lessons: completedLessons,
                    progress_percentage: chapterProgress,
                    total_time_seconds: Number(chapter.total_time_seconds || 0),
                    is_started: Boolean(chapter.is_started || chapterProgress > 0),
                    is_completed: Boolean(chapter.is_completed || chapterProgress >= 100),
                    lessons,
                };
            })
            .sort((first, second) => first.order - second.order);

        const orderedLessons = chapterRows.flatMap((chapter) =>
            chapter.lessons.map((lesson) => ({
                ...lesson,
                chapterId: chapter.id,
            }))
        );

        const unlockedLessonIds = new Set(
            orderedLessons
                .filter((lesson) => lesson.status === "completed")
                .map((lesson) => lesson.id)
        );

        const currentLesson = orderedLessons.find((lesson) => lesson.status !== "completed");

        if (currentLesson) {
            unlockedLessonIds.add(currentLesson.id);
        } else {
            orderedLessons.forEach((lesson) => unlockedLessonIds.add(lesson.id));
        }

        return chapterRows.map((chapter) => ({
            ...chapter,
            has_bookmark: chapter.lessons.some((lesson) => bookmarkedLessonIds.has(lesson.id)),
            lessons: chapter.lessons.map((lesson) => ({
                ...lesson,
                is_locked: !unlockedLessonIds.has(lesson.id),
                is_bookmarked: bookmarkedLessonIds.has(lesson.id),
            })),
        }));
    }, [bookData?.chapters, bookmarkedLessonIds, progressData?.chapters]);

    const learningSummary = useMemo(() => {
        const totalLessons = chapters.reduce((sum, chapter) => sum + chapter.total_lessons, 0);
        const completedLessons = chapters.reduce((sum, chapter) => sum + chapter.completed_lessons, 0);
        const completedChapters = chapters.filter((chapter) => chapter.is_completed).length;
        const totalTime = chapters.reduce((sum, chapter) => sum + chapter.total_time_seconds, 0);
        const nextLesson = chapters
            .flatMap((chapter) => chapter.lessons.map((lesson) => ({ ...lesson, chapterTitle: chapter.title })))
            .find((lesson) => lesson.status !== "completed");

        return {
            totalLessons,
            completedLessons,
            completedChapters,
            totalTime,
            nextLesson,
        };
    }, [chapters]);

    const defaultOpenChapterKey = useMemo(() => {
        const activeChapterIndex = chapters.findIndex((chapter) =>
            chapter.lessons.some((lesson) => !lesson.is_locked)
            && chapter.lessons.some((lesson) => lesson.status !== "completed")
        );

        return activeChapterIndex >= 0 ? activeChapterIndex.toString() : undefined;
    }, [chapters]);

    const normalizedReviews = useMemo(() => {
        const combined = [
            ...(Array.isArray(reviews) ? reviews : []),
            ...(currentReview ? [currentReview] : []),
        ];
        const seen = new Set();

        return combined
            .filter((review) => {
                if (!review || seen.has(review.id)) return false;
                seen.add(review.id);
                return true;
            })
            .map((review) => ({
                ...review,
                rating: Number(review.rating || 0),
                author_name: getAuthorName(review),
            }));
    }, [currentReview, reviews]);

    const normalizedBookmarks = useMemo(() => (
        (Array.isArray(bookmarks) ? bookmarks : []).map((bookmark) => ({
            ...bookmark,
            slide: Number(bookmark.slide || 0),
            slide_order: Number(bookmark.slide_order || 0),
            lesson_id: Number(bookmark.lesson_id || 0),
        }))
    ), [bookmarks]);

    const normalizedNotes = useMemo(() => (
        (Array.isArray(notes) ? notes : []).map((note) => ({
            ...note,
            slide: Number(note.slide || 0),
            slide_order: Number(note.slide_order || 0),
            lesson_id: Number(note.lesson_id || 0),
            x_percent: Number(note.x_percent || 0),
            y_percent: Number(note.y_percent || 0),
        }))
    ), [notes]);

    const reviewSummary = useMemo(() => {
        const apiTotal = Number(bookData?.review_summary?.total_reviews || book?.total_reviews || 0);
        const apiAverage = Number(bookData?.review_summary?.average_rating || book?.average_rating || 0);
        const localTotal = normalizedReviews.length;
        const localAverage = localTotal
            ? normalizedReviews.reduce((sum, review) => sum + review.rating, 0) / localTotal
            : 0;

        return {
            total: Math.max(apiTotal, localTotal),
            average: apiTotal > 0 ? apiAverage : localAverage,
        };
    }, [book?.average_rating, book?.total_reviews, bookData?.review_summary, normalizedReviews]);

    const selectTab = (tabKey) => {
        setActiveTab(tabKey);
        window.requestAnimationFrame(() => {
            contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    const openLessonReader = (lesson) => {
        if (!lesson?.id || !slug || lesson.is_locked) return;
        navigate(`${ROUTES.BOOKS_LESSON_READER}/${slug}/${lesson.id}`);
    };

    const openBookmarkedSlide = (bookmark) => {
        if (!bookmark?.lesson_id || !bookmark?.slide || !slug) return;
        navigate(`${ROUTES.BOOKS_LESSON_READER}/${slug}/${bookmark.lesson_id}?slide_id=${bookmark.slide}`);
    };

    const deleteBookmark = async (bookmarkId) => {
        if (!bookmarkId) return;

        setIsActionLoading(true);
        const response = await deleteBookBookmarkService(bookmarkId, {});
        setIsActionLoading(false);

        if (response === "error") {
            clearData();
            navigate(ROUTES.LOGIN);
            return;
        }

        if (response.status_code === 200 || response.status_code === 201) {
            setBookmarks((previous) => previous.filter((bookmark) => bookmark.id !== bookmarkId));
            return;
        }
    };

    const openNote = (note) => {
        if (!note?.lesson_id || !note?.slide || !slug) return;
        navigate(`${ROUTES.BOOKS_LESSON_READER}/${slug}/${note.lesson_id}?slide_id=${note.slide}`);
    };

    const deleteNote = async (noteId) => {
        if (!noteId) return;

        setIsActionLoading(true);
        const response = await deleteBookNoteService(noteId, {});
        setIsActionLoading(false);

        if (response === "error") {
            clearData();
            navigate(ROUTES.LOGIN);
            return;
        }

        if (response.status_code === 200 || response.status_code === 201) {
            setNotes((previous) => previous.filter((note) => note.id !== noteId));
        }
    };

    const validateReview = () => {
        const errors = {};

        if (!reviewForm.rating) {
            errors.rating = "Select a rating.";
        }

        if (!reviewForm.comment.trim()) {
            errors.comment = "Write a review before submitting.";
        } else if (reviewForm.comment.trim().length < 10) {
            errors.comment = "Write at least 10 characters.";
        }

        setReviewErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleReviewField = (field, value) => {
        setReviewForm((previous) => ({ ...previous, [field]: value }));
        setReviewErrors((previous) => ({ ...previous, [field]: "" }));
    };

    const submitReview = async () => {
        if (!book?.id || (!canReview && !currentReview) || !validateReview()) return;

        setIsActionLoading(true);
        setReviewMessage({ type: "", text: "" });

        const payload = {
            rating: reviewForm.rating,
            title: reviewForm.title.trim(),
            comment: reviewForm.comment.trim(),
        };

        const response = currentReview
            ? await updateBookReviewService(currentReview.id, payload, {})
            : await createBookReviewService({ ...payload, book: book.id }, {});

        setIsActionLoading(false);

        if (response === "error") {
            clearData();
            navigate(ROUTES.LOGIN);
            return;
        }

        if (response.status_code === 200 || response.status_code === 201) {
            setReviewMessage({
                type: "success",
                text: currentReview ? "Your review has been updated." : "Your review has been submitted.",
            });
            await loadPageData();
            selectTab("reviews");
            return;
        }

        setReviewMessage({
            type: "danger",
            text: response?.message?.[0] || "Unable to save your review right now.",
        });
    };

    const deleteReview = async () => {
        if (!currentReview) return;

        const confirmed = window.confirm("Delete your review for this book?");
        if (!confirmed) return;

        setIsActionLoading(true);
        const response = await deleteBookReviewService(currentReview.id, {});
        setIsActionLoading(false);

        if (response === "error") {
            clearData();
            navigate(ROUTES.LOGIN);
            return;
        }

        if (response.status_code === 200 || response.status_code === 201) {
            setReviewForm(defaultReviewForm);
            setReviewMessage({ type: "success", text: "Your review has been deleted." });
            await loadPageData();
            selectTab("reviews");
            return;
        }

        setReviewMessage({
            type: "danger",
            text: response?.message?.[0] || "Unable to delete your review right now.",
        });
    };

    const renderStars = (rating, interactive = false) => (
        <div className={`book-dd-stars ${interactive ? "book-dd-stars-interactive" : ""}`}>
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    aria-label={`${star} star`}
                    className={`book-dd-star ${star <= rating ? "is-active" : ""}`}
                    onClick={interactive ? () => handleReviewField("rating", star) : undefined}
                    disabled={!interactive}
                >
                    <i className="bi bi-star-fill"></i>
                </button>
            ))}
        </div>
    );

    const renderSkeleton = () => (
        <div className="academic-courses-container" style={{ display: "flex" }}>
            <SideBar activeItem={sidebarNavItems.bookDashboard} />
            <div className="courses-side-container">
                <TopNavigation activeItem={sidebarNavItems.bookDashboard} />
                <main className="book-dd-page book-dd-page-loading" aria-busy="true" aria-label="Loading book details">
                    <Container fluid className="book-dd-shell">
                        <section className="book-dd-header book-dd-skeleton-wrap">
                            <div className="book-dd-cover-panel">
                                <div className="book-dd-cover book-dd-skeleton book-dd-skeleton-cover"></div>
                            </div>

                            <div className="book-dd-intro">
                                <div className="book-dd-kicker-row">
                                    <div className="book-dd-skeleton book-dd-skeleton-pill"></div>
                                    <div className="book-dd-skeleton book-dd-skeleton-badge"></div>
                                </div>
                                <div className="book-dd-skeleton book-dd-skeleton-title"></div>
                                <div className="book-dd-skeleton book-dd-skeleton-subtitle"></div>
                                <div className="book-dd-skeleton-lines">
                                    <div className="book-dd-skeleton book-dd-skeleton-line"></div>
                                    <div className="book-dd-skeleton book-dd-skeleton-line medium"></div>
                                </div>
                                <div className="book-dd-skeleton-meta">
                                    {[1, 2, 3].map((item) => (
                                        <div key={item} className="book-dd-skeleton book-dd-skeleton-chip"></div>
                                    ))}
                                </div>
                                <div className="book-dd-progress-block book-dd-skeleton-card">
                                    <div className="book-dd-progress-top">
                                        <div className="book-dd-skeleton book-dd-skeleton-label"></div>
                                        <div className="book-dd-skeleton book-dd-skeleton-percent"></div>
                                    </div>
                                    <div className="book-dd-skeleton book-dd-skeleton-meter"></div>
                                    <div className="book-dd-progress-bottom">
                                        <div className="book-dd-skeleton book-dd-skeleton-label short"></div>
                                        <div className="book-dd-skeleton book-dd-skeleton-label"></div>
                                    </div>
                                </div>
                            </div>

                            <aside className="book-dd-focus-panel book-dd-skeleton-focus">
                                <div className="book-dd-next-head">
                                    <div className="book-dd-skeleton book-dd-skeleton-label light"></div>
                                    <div className="book-dd-skeleton book-dd-skeleton-dot"></div>
                                </div>
                                <div className="book-dd-next-body">
                                    <div className="book-dd-skeleton book-dd-skeleton-next-icon"></div>
                                    <div>
                                        <div className="book-dd-skeleton book-dd-skeleton-heading light"></div>
                                        <div className="book-dd-skeleton book-dd-skeleton-label light wide"></div>
                                    </div>
                                </div>
                                <div className="book-dd-mini-grid">
                                    {[1, 2].map((item) => (
                                        <div key={item} className="book-dd-skeleton-mini">
                                            <div className="book-dd-skeleton book-dd-skeleton-label light"></div>
                                            <div className="book-dd-skeleton book-dd-skeleton-label light short"></div>
                                        </div>
                                    ))}
                                </div>
                                <div className="book-dd-next-context">
                                    {[1, 2, 3].map((item) => (
                                        <div key={item} className="book-dd-skeleton-next-context">
                                            <div className="book-dd-skeleton book-dd-skeleton-context-icon"></div>
                                            <div className="book-dd-skeleton book-dd-skeleton-label light short"></div>
                                            <div className="book-dd-skeleton book-dd-skeleton-label light"></div>
                                        </div>
                                    ))}
                                </div>
                                <div className="book-dd-skeleton book-dd-skeleton-button"></div>
                            </aside>
                        </section>

                        <section className="book-dd-stats book-dd-skeleton-stats">
                            {[1, 2, 3, 4].map((item) => (
                                <div key={item} className="book-dd-stat">
                                    <div className="book-dd-skeleton book-dd-skeleton-stat-icon"></div>
                                    <div className="book-dd-skeleton book-dd-skeleton-label"></div>
                                    <div className="book-dd-skeleton book-dd-skeleton-heading"></div>
                                </div>
                            ))}
                        </section>

                        <section className="book-dd-workspace book-dd-skeleton-workspace">
                            <div className="book-dd-tabbar">
                                {[1, 2, 3].map((item) => (
                                    <div key={item} className="book-dd-skeleton book-dd-skeleton-tab"></div>
                                ))}
                            </div>
                            <div className="book-dd-panel">
                                <div className="book-dd-section-heading">
                                    <div>
                                        <div className="book-dd-skeleton book-dd-skeleton-label"></div>
                                        <div className="book-dd-skeleton book-dd-skeleton-heading large"></div>
                                    </div>
                                    <div className="book-dd-skeleton book-dd-skeleton-chip"></div>
                                </div>
                                <div className="book-dd-skeleton-chapters">
                                    {[1, 2, 3].map((item) => (
                                        <div key={item} className="book-dd-skeleton-chapter">
                                            <div className="book-dd-skeleton book-dd-skeleton-chapter-number"></div>
                                            <div>
                                                <div className="book-dd-skeleton book-dd-skeleton-heading"></div>
                                                <div className="book-dd-skeleton book-dd-skeleton-line medium"></div>
                                            </div>
                                            <div className="book-dd-skeleton book-dd-skeleton-chip"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>
                    </Container>
                </main>
            </div>
        </div>
    );

    if (isLoading) {
        return renderSkeleton();
    }

    if (!bookData || !book) {
        return (
            <div className="academic-courses-container" style={{ display: "flex" }}>
                <SideBar activeItem={sidebarNavItems.bookDashboard} />
                <div className="courses-side-container">
                    <TopNavigation activeItem={sidebarNavItems.bookDashboard} />
                    <main className="book-dd-page">
                        <section className="book-dd-empty-state">
                            <h2>Book details unavailable</h2>
                            <p>{pageError || "We could not load this purchased book."}</p>
                            <Button onClick={() => navigate(ROUTES.BOOKS_SELLING_BOOK_DASHBOARD)}>
                                Back to My Books
                            </Button>
                        </section>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="academic-courses-container" style={{ display: "flex" }}>
            <SideBar activeItem={sidebarNavItems.bookDashboard} />
            <div className="courses-side-container">
                <TopNavigation activeItem={sidebarNavItems.bookDashboard} />

                <main className="book-dd-page">
                    <Container fluid className="book-dd-shell">
                        <section className="book-dd-header">
                            <div className="book-dd-cover-panel">
                                {book.cover_image_url ? (
                                    <img src={book.cover_image_url} alt={book.title} className="book-dd-cover" />
                                ) : (
                                    <div className="book-dd-cover book-dd-cover-fallback">{book.title}</div>
                                )}
                            </div>

                            <div className="book-dd-intro">
                                <div className="book-dd-kicker-row">
                                    <button
                                        type="button"
                                        className="book-dd-back-btn"
                                        onClick={() => navigate(ROUTES.BOOKS_SELLING_BOOK_DASHBOARD)}
                                    >
                                        <i className="bi bi-arrow-left"></i>
                                        My Books
                                    </button>
                                    <Badge className={`book-dd-status ${learningStatus}`}>
                                        {statusCopy[learningStatus]}
                                    </Badge>
                                </div>

                                <h1>{book.title}</h1>
                                {book.subtitle && <p className="book-dd-subtitle">{book.subtitle}</p>}
                                <p className="book-dd-description">{book.short_description || book.description}</p>

                                <div className="book-dd-meta-row">
                                    <span>
                                        <i className="bi bi-book"></i>
                                        {chapters.length} chapters
                                    </span>
                                    <span>
                                        <i className="bi bi-journal-check"></i>
                                        {learningSummary.totalLessons} lessons
                                    </span>
                                    <span>
                                        <i className="bi bi-star-fill"></i>
                                        {reviewSummary.average.toFixed(2)} rating
                                    </span>
                                </div>

                                <div className="book-dd-progress-block">
                                    <div className="book-dd-progress-top">
                                        <span>Overall progress</span>
                                        <strong>{progress}%</strong>
                                    </div>
                                    <ProgressBar now={progress} className="book-dd-progress" />
                                    <div className="book-dd-progress-bottom">
                                        <span>{learningSummary.completedLessons} of {learningSummary.totalLessons} lessons complete</span>
                                        <span>Purchased {formatDate(bookData.purchased_at)}</span>
                                    </div>
                                </div>

                            </div>

                            <aside className="book-dd-focus-panel">
                                <div className="book-dd-next-head">
                                    <span className="book-dd-panel-label">Next Up</span>
                                    <span className={`book-dd-next-dot ${learningStatus}`}></span>
                                </div>

                                <div className="book-dd-next-body">
                                    <div className="book-dd-next-icon">
                                        <i className={learningSummary.nextLesson ? "bi bi-play-fill" : "bi bi-check2"}></i>
                                    </div>
                                    <div>
                                        <h3>{learningSummary.nextLesson?.title || "All caught up"}</h3>
                                        <p>{learningSummary.nextLesson?.chapterTitle || "You have completed the available lessons."}</p>
                                    </div>
                                </div>

                                <div className="book-dd-mini-grid">
                                    <div>
                                        <strong>{learningSummary.completedChapters}/{chapters.length}</strong>
                                        <span>Chapters</span>
                                    </div>
                                    <div>
                                        <strong>{formatTime(learningSummary.totalTime)}</strong>
                                        <span>Study time</span>
                                    </div>
                                </div>

                                <div className="book-dd-next-context">
                                    <div>
                                        <i className="bi bi-collection-play"></i>
                                        <span>Format</span>
                                        <strong>{learningSummary.nextLesson?.content_type || "Lesson"}</strong>
                                    </div>
                                    <div>
                                        <i className="bi bi-hourglass-split"></i>
                                        <span>Estimated time</span>
                                        <strong>
                                            {learningSummary.nextLesson?.duration_minutes
                                                ? `${learningSummary.nextLesson.duration_minutes} min`
                                                : "Self-paced"}
                                        </strong>
                                    </div>
                                    <div>
                                        <i className="bi bi-flag"></i>
                                        <span>Status</span>
                                        <strong>{statusCopy[learningSummary.nextLesson?.status] || statusCopy[learningStatus]}</strong>
                                    </div>
                                </div>

                                <Button
                                    className="book-dd-next-action"
                                    onClick={() => (
                                        learningSummary.nextLesson?.id
                                            ? openLessonReader(learningSummary.nextLesson)
                                            : selectTab("chapters")
                                    )}
                                >
                                    <i className="bi bi-play-circle-fill"></i>
                                    {progress > 0 ? "Continue Learning" : "Start Learning"}
                                </Button>
                            </aside>
                        </section>

                        <section className="book-dd-stats">
                            <div className="book-dd-stat">
                                <i className="bi bi-check2-circle"></i>
                                <span>Lessons Completed</span>
                                <strong>{learningSummary.completedLessons}/{learningSummary.totalLessons}</strong>
                            </div>
                            <div className="book-dd-stat">
                                <i className="bi bi-bar-chart-line"></i>
                                <span>Completion</span>
                                <strong>{progress}%</strong>
                            </div>
                            <div className="book-dd-stat">
                                <i className="bi bi-clock-history"></i>
                                <span>Recorded Time</span>
                                <strong>{formatTime(learningSummary.totalTime)}</strong>
                            </div>
                            <div className="book-dd-stat">
                                <i className="bi bi-calendar-check"></i>
                                <span>Last Accessed</span>
                                <strong>{formatDate(bookData.last_accessed_at)}</strong>
                            </div>
                        </section>

                        <section ref={contentRef} className="book-dd-workspace">
                            <div className="book-dd-tabbar" role="tablist">
                                {[
                                    ["chapters", "Chapters & Lessons", "bi-list-task"],
                                    ["overview", "Overview", "bi-info-circle"],
                                    ["bookmarks", `Bookmarks (${normalizedBookmarks.length})`, "bi-bookmark"],
                                    ["notes", `Notes (${normalizedNotes.length})`, "bi-stickies"],
                                    ["reviews", `Reviews (${reviewSummary.total})`, "bi-star"],
                                ].map(([key, label, icon]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={activeTab === key ? "active" : ""}
                                        onClick={() => setActiveTab(key)}
                                    >
                                        <i className={`bi ${icon}`}></i>
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {activeTab === "chapters" && (
                                <section className="book-dd-panel">
                                    <div className="book-dd-section-heading">
                                        <div>
                                            <span>Course Outline</span>
                                            <h2>Chapters and lessons</h2>
                                        </div>
                                        <p>{learningSummary.completedLessons} lessons completed</p>
                                    </div>

                                    {chapters.length > 0 ? (
                                        <Accordion defaultActiveKey={defaultOpenChapterKey} className="book-dd-outline">
                                            {chapters.map((chapter, index) => (
                                                <Accordion.Item eventKey={index.toString()} key={chapter.id}>
                                                    <Accordion.Header>
                                                        <div className="book-dd-chapter-head">
                                                            <div className="book-dd-chapter-number">{String(chapter.order).padStart(2, "0")}</div>
                                                            <div className="book-dd-chapter-title">
                                                                <h3>{chapter.title}</h3>
                                                                <p>{chapter.completed_lessons}/{chapter.total_lessons} lessons | {formatTime(chapter.total_time_seconds)}{chapter.has_bookmark ? " | Bookmarked" : ""}</p>
                                                            </div>
                                                            <span className={`book-dd-chapter-pill ${chapter.is_completed ? "done" : chapter.is_started ? "active" : ""}`}>
                                                                {chapter.progress_percentage}%
                                                            </span>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        {chapter.description && <p className="book-dd-chapter-desc">{chapter.description}</p>}
                                                        <ProgressBar now={chapter.progress_percentage} className="book-dd-chapter-progress" />

                                                        {chapter.lessons.length > 0 ? (
                                                            <div className="book-dd-lessons">
                                                                {chapter.lessons.map((lesson) => (
                                                                    <div
                                                                        key={lesson.id}
                                                                        className={`book-dd-lesson ${lesson.is_locked ? "is-locked" : ""}`}
                                                                        role={lesson.is_locked ? undefined : "button"}
                                                                        tabIndex={lesson.is_locked ? -1 : 0}
                                                                        onClick={() => openLessonReader(lesson)}
                                                                        onKeyDown={(event) => {
                                                                            if (!lesson.is_locked && (event.key === "Enter" || event.key === " ")) {
                                                                                event.preventDefault();
                                                                                openLessonReader(lesson);
                                                                            }
                                                                        }}
                                                                        aria-disabled={lesson.is_locked}
                                                                        title={lesson.is_locked ? "Complete the current lesson to unlock this lesson." : ""}
                                                                    >
                                                                        <div className={`book-dd-lesson-state ${lesson.is_locked ? "locked" : lesson.status}`}>
                                                                            <i className={`bi ${lesson.is_locked ? "bi-lock-fill" : lesson.status === "completed" ? "bi-check-lg" : lesson.status === "in_progress" ? "bi-play-fill" : "bi-circle"}`}></i>
                                                                        </div>
                                                                        <div className="book-dd-lesson-main">
                                                                            <h4>{lesson.title}</h4>
                                                                            {lesson.is_bookmarked && (
                                                                                <span className="book-dd-lesson-bookmark-label">Bookmarked</span>
                                                                            )}
                                                                            <p>
                                                                                {lesson.is_locked
                                                                                    ? "Complete the current lesson to unlock"
                                                                                    : `${lesson.content_type} | ${lesson.duration_minutes ? `${lesson.duration_minutes} min` : "Self-paced"} | ${formatTime(lesson.time_spent_seconds)}`}
                                                                            </p>
                                                                        </div>
                                                                        <Badge className={`book-dd-lesson-badge ${lesson.is_locked ? "locked" : lesson.status}`}>
                                                                            {lesson.is_locked ? statusCopy.locked : statusCopy[lesson.status] || "Not Started"}
                                                                        </Badge>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="book-dd-muted">Lessons will appear here once they are published.</p>
                                                        )}
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            ))}
                                        </Accordion>
                                    ) : (
                                        <div className="book-dd-empty-state compact">
                                            <h3>No chapters available</h3>
                                            <p>This book does not have published chapters yet.</p>
                                        </div>
                                    )}
                                </section>
                            )}

                            {activeTab === "overview" && (
                                <section className="book-dd-panel">
                                    <div className="book-dd-section-heading">
                                        <div>
                                            <span>Book Details</span>
                                            <h2>Overview</h2>
                                        </div>
                                    </div>
                                    <Row className="g-4">
                                        <Col lg={8}>
                                            <div className="book-dd-overview-copy">
                                                <h3>About this book</h3>
                                                <p>{book.description || "No description has been added yet."}</p>
                                            </div>
                                        </Col>
                                        <Col lg={4}>
                                            <div className="book-dd-detail-list">
                                                <div>
                                                    <span>Subject</span>
                                                    <strong>{book.subject_display || book.subject || "Not available"}</strong>
                                                </div>
                                                <div>
                                                    <span>Grades</span>
                                                    <strong>
                                                        {(book.grade_level || []).length
                                                            ? book.grade_level.map((grade) => `Grade ${grade.name}`).join(", ")
                                                            : "Not available"}
                                                    </strong>
                                                </div>
                                                <div>
                                                    <span>Paid</span>
                                                    <strong>Rs. {bookData.price_paid}</strong>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </section>
                            )}

                            {activeTab === "bookmarks" && (
                                <section className="book-dd-panel">
                                    <div className="book-dd-section-heading">
                                        <div>
                                            <span>Saved Pages</span>
                                            <h2>Bookmarks</h2>
                                        </div>
                                        <p>{normalizedBookmarks.length} saved {normalizedBookmarks.length === 1 ? "page" : "pages"}</p>
                                    </div>

                                    {normalizedBookmarks.length > 0 ? (
                                        <div className="book-dd-bookmark-list">
                                            {normalizedBookmarks.map((bookmark) => (
                                                <article key={bookmark.id} className="book-dd-bookmark-item">
                                                    <div className="book-dd-bookmark-main">
                                                        <div className="book-dd-bookmark-badge">
                                                            <i className="bi bi-bookmark-fill"></i>
                                                            <span>Page {bookmark.slide_order || bookmark.slide}</span>
                                                        </div>
                                                        <div>
                                                            <h4>{bookmark.slide_title || `Page ${bookmark.slide_order}`}</h4>
                                                            <p>{bookmark.chapter_title} | {bookmark.lesson_title}</p>
                                                            {bookmark.title && <strong>{bookmark.title}</strong>}
                                                            {bookmark.note && <p className="book-dd-bookmark-note">{bookmark.note}</p>}
                                                        </div>
                                                    </div>
                                                    <div className="book-dd-bookmark-side">
                                                        <span>{formatDate(bookmark.created_at)}</span>
                                                        <div className="book-dd-bookmark-actions">
                                                            <Button
                                                                className="book-dd-primary-btn"
                                                                onClick={() => openBookmarkedSlide(bookmark)}
                                                            >
                                                                Open
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                onClick={() => deleteBookmark(bookmark.id)}
                                                                disabled={isActionLoading}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="book-dd-empty-state compact">
                                            <h3>No bookmarks yet</h3>
                                            <p>Save pages while reading and they will appear here for quick access.</p>
                                        </div>
                                    )}
                                </section>
                            )}

                            {activeTab === "notes" && (
                                <section className="book-dd-panel">
                                    <div className="book-dd-section-heading">
                                        <div>
                                            <span>Saved Thoughts</span>
                                            <h2>Notes</h2>
                                        </div>
                                        <p>{normalizedNotes.length} saved {normalizedNotes.length === 1 ? "note" : "notes"}</p>
                                    </div>

                                    {normalizedNotes.length > 0 ? (
                                        <div className="book-dd-bookmark-list">
                                            {normalizedNotes.map((note) => (
                                                <article key={note.id} className="book-dd-bookmark-item book-dd-note-item">
                                                    <div className="book-dd-bookmark-main">
                                                        <div className="book-dd-bookmark-badge book-dd-note-badge">
                                                            <i className="bi bi-stickies-fill"></i>
                                                            <span>Page {note.slide_order || note.slide}</span>
                                                        </div>
                                                        <div>
                                                            <h4>{note.slide_title || `Page ${note.slide_order}`}</h4>
                                                            <p>{note.chapter_title} | {note.lesson_title}</p>
                                                            <p className="book-dd-note-preview">{note.content}</p>
                                                        </div>
                                                    </div>
                                                    <div className="book-dd-bookmark-side">
                                                        <span>{formatDate(note.updated_at || note.created_at)}</span>
                                                        <div className="book-dd-bookmark-actions">
                                                            <Button
                                                                className="book-dd-primary-btn"
                                                                onClick={() => openNote(note)}
                                                            >
                                                                Open
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                onClick={() => deleteNote(note.id)}
                                                                disabled={isActionLoading}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="book-dd-empty-state compact">
                                            <h3>No notes yet</h3>
                                            <p>Add notes while reading and they will appear here for quick access.</p>
                                        </div>
                                    )}
                                </section>
                            )}

                            {activeTab === "reviews" && (
                                <section className="book-dd-panel">
                                    <div className="book-dd-section-heading">
                                        <div>
                                            <span>Feedback</span>
                                            <h2>Reviews</h2>
                                        </div>
                                        <p>{reviewSummary.average.toFixed(2)} average rating</p>
                                    </div>

                                    <Row className="g-4">
                                        <Col xl={4}>
                                            <div className="book-dd-review-summary">
                                                <strong>{reviewSummary.average.toFixed(2)}</strong>
                                                {renderStars(Math.round(reviewSummary.average))}
                                                <span>{reviewSummary.total} total reviews</span>
                                            </div>
                                        </Col>
                                        <Col xl={8}>
                                            <div className="book-dd-review-form">
                                                <h3>{currentReview ? "Update your review" : "Write a review"}</h3>
                                                {reviewMessage.text && (
                                                    <Alert variant={reviewMessage.type} className="book-dd-alert">
                                                        {reviewMessage.text}
                                                    </Alert>
                                                )}
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Rating</Form.Label>
                                                    {renderStars(reviewForm.rating, true)}
                                                    {reviewErrors.rating && <div className="book-dd-field-error">{reviewErrors.rating}</div>}
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Title</Form.Label>
                                                    <Form.Control
                                                        value={reviewForm.title}
                                                        placeholder="Short summary"
                                                        onChange={(event) => handleReviewField("title", event.target.value)}
                                                    />
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Review</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={4}
                                                        value={reviewForm.comment}
                                                        placeholder="Share what helped you learn"
                                                        onChange={(event) => handleReviewField("comment", event.target.value)}
                                                    />
                                                    {reviewErrors.comment && <div className="book-dd-field-error">{reviewErrors.comment}</div>}
                                                </Form.Group>
                                                <div className="book-dd-review-actions">
                                                    <Button
                                                        className="book-dd-primary-btn"
                                                        onClick={submitReview}
                                                        disabled={isActionLoading || (!canReview && !currentReview)}
                                                    >
                                                        {isActionLoading ? "Saving..." : currentReview ? "Update Review" : "Submit Review"}
                                                    </Button>
                                                    {currentReview && (
                                                        <>
                                                            <Button variant="outline-secondary" onClick={() => syncReviewForm(currentReview)} disabled={isActionLoading}>
                                                                Reset
                                                            </Button>
                                                            <Button variant="outline-danger" onClick={deleteReview} disabled={isActionLoading}>
                                                                Delete
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    <div className="book-dd-review-list">
                                        {normalizedReviews.length > 0 ? (
                                            normalizedReviews.map((review) => (
                                                <article key={review.id} className="book-dd-review-item">
                                                    <div>
                                                        <h4>{review.author_name}</h4>
                                                        {renderStars(review.rating)}
                                                    </div>
                                                    <span>{formatDate(review.created_at)}</span>
                                                    {review.title && <h5>{review.title}</h5>}
                                                    <p>{review.comment}</p>
                                                </article>
                                            ))
                                        ) : (
                                            <div className="book-dd-empty-state compact">
                                                <h3>No reviews yet</h3>
                                                <p>Your review will appear here after submission.</p>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            )}
                        </section>
                    </Container>
                </main>
            </div>
        </div>
    );
};

export default PurchasedBookDetails;
